import type { Session } from "@/features/auth/api/session.api";

/**
 * What the shell should say about the licence, if anything.
 *
 * THE SERVER ALREADY ANSWERS 402 on every business route when a subscription has lapsed, with a
 * specific Turkish sentence. That is the enforcement and it is not enough on its own, because it
 * arrives only once somebody has ALREADY been stopped, on whichever screen they happened to
 * open — so a customer's first news of their lapsed subscription is an error on a page they were
 * trying to work on, and it looks like a bug. These two functions turn the same fact into one
 * notice, shown before anything fails.
 *
 * THE THRESHOLD IS A CLIENT DECISION, which is why the server sends a number of days rather
 * than a boolean. Changing when the warning starts needs no server release, and two surfaces
 * that want different thresholds (a banner at 14 days, a louder one at 3) both read the same
 * field.
 */

/** Days out at which the countdown starts being shown. Two weeks is a commercial conversation. */
export const EXPIRY_WARNING_DAYS = 14;

/**
 * Whether the product is closed to this customer right now.
 *
 * When true the shell shows the reason INSTEAD of the application. Not a dismissible banner: a
 * blocked customer clicking into screens that each answer 402 separately learns nothing except
 * that the product is broken, and every one of those requests is work the server does to refuse.
 */
export function isSubscriptionBlocked(session: Session): boolean {
  return session.subscription.blockedReason !== null;
}

/**
 * The countdown, or null when there is nothing to say.
 *
 * Null for a perpetual package (a gift or a pilot, which the product allows), for anything
 * further out than the threshold, and for a customer already blocked — they get the blocking
 * notice, and a countdown beside it would be a second, weaker statement of the same thing.
 */
export function expiryWarning(session: Session): { days: number } | null {
  const { daysRemaining, blockedReason } = session.subscription;
  if (blockedReason !== null) return null;
  if (daysRemaining === null) return null;
  if (daysRemaining > EXPIRY_WARNING_DAYS) return null;
  return { days: daysRemaining };
}

/** "3 gün sonra", "bugün" — the phrase, so the two surfaces cannot word it differently. */
export function expiryPhrase(days: number): string {
  // Zero is today, not "0 gün sonra". Somebody reading that would have to work out what it
  // means, and what it means is that they have hours.
  return days === 0 ? "bugün" : `${days} gün sonra`;
}
